import { cn } from '../../lib/utils';
import {
  ADMISSION_PAYMENT_META,
  normalizeAdmissionPaymentState,
} from '../../utils/admissionPayment';
import type { AdmissionPaymentState } from '../../utils/admissionPayment';

interface AdmissionPaymentBadgeProps {
  status?: AdmissionPaymentState | string | null;
  /** `sm` for list rows, `md` for the detail page and modal hero. */
  size?: 'sm' | 'md';
  /** Extra detail rendered after the label (e.g. the outstanding amount). */
  detail?: string;
  className?: string;
  /** Overrides the default explanation shown on hover. */
  title?: string;
}

/**
 * One consistent payment-state pill for the IPD list, admission detail page and
 * discharged-admission modal. Risk Fund is always highlighted so a written-off
 * balance is never read as "Paid".
 */
export function AdmissionPaymentBadge({
  status,
  size = 'sm',
  detail,
  className,
  title,
}: AdmissionPaymentBadgeProps) {
  const state = normalizeAdmissionPaymentState(status) ?? 'NO_INVOICE';
  const meta = ADMISSION_PAYMENT_META[state];
  const Icon = meta.icon;
  const isRiskFund = state === 'RISK_FUND';
  // Risk Fund always gets the loud treatment; other states only step up to it
  // on the larger detail surfaces.
  const tone = isRiskFund || size === 'md' ? meta.highlight : meta.chip;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full font-bold uppercase tracking-wide',
        size === 'sm' ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1 text-[11px] sm:text-xs',
        tone,
        className,
      )}
      title={title ?? meta.description}
      data-payment-state={state}
    >
      <Icon size={size === 'sm' ? 11 : 13} className="shrink-0" aria-hidden="true" />
      {meta.label}
      {detail && (
        <span className="font-semibold normal-case tracking-normal opacity-90">
          · {detail}
        </span>
      )}
    </span>
  );
}
